import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { findArticle, placeMeta } from "../data/travel";
import type { TravelBlock, TravelFigure } from "../types";
import useTransitionTo from "../utils/useTransitionTo";
import Contact from "./Contact";
import Nav from "./Nav";
import ReadProgress from "./ReadProgress";

// Landscape photos share one 16:9 frame, wider than the text column on laptops;
// portrait photos stay uncropped (3:4) and centred
const Figure = ({ f, portrait }: { f: TravelFigure; portrait?: boolean }) => (
  <figure className={"ta-fig" + (portrait ? " is-portrait" : "")}>
    <div className="ta-fig-img">
      {f.src && (
        <img
          src={f.src}
          srcSet={f.srcSet}
          sizes={portrait ? "(max-width: 820px) 100vw, 560px" : "(max-width: 820px) 100vw, 1000px"}
          alt={f.alt || ""}
          loading="lazy"
          onError={(e) => e.currentTarget.parentElement?.classList.add("no-img")}
        />
      )}
      <span className="ta-fig-ph" aria-hidden="true">photo coming</span>
    </div>
    {f.caption && <figcaption className="ta-fig-cap">{f.caption}</figcaption>}
  </figure>
);

const Block = ({ b }: { b: TravelBlock }) => {
  if (b.t === "quote") return <blockquote className="es-quote">{b.text}</blockquote>;
  if (b.t === "h") return <h2 className="ta-h">{b.text}</h2>;
  if (b.t === "divider") return <div className="es-divider" aria-hidden="true">&#10038;</div>;
  if (b.t === "photo") return <Figure f={b} portrait={b.portrait} />;
  return <p className={"es-p" + (b.drop ? " es-drop" : "")}>{b.text}</p>;
};

const TravelArticlePage = () => {
  const { slug } = useParams();
  const a = findArticle(slug);
  const transitionTo = useTransitionTo();

  useEffect(() => {
    if (a) document.title = `${a.title} — Megholova`;
  }, [a]);

  if (!a) return <Navigate to="/travel" replace />;

  const toTravel = (e: React.MouseEvent) => {
    e.preventDefault();
    transitionTo("/travel");
  };

  return (
    <>
      <ReadProgress />
      <Nav />
      <main>
        <article className="ta">
          <header className="ta-hero">
            {a.hero && (
              <img className="ta-hero-img" src={a.hero.src} srcSet={a.hero.srcSet} sizes="100vw" alt="" />
            )}
            <div className="ta-hero-overlay" />
            <div className="ta-hero-content wrap-read">
              <a className="page-back light" href="/travel" onClick={toTravel}>← Field Notes</a>
              <div className="ta-place">
                <span className="ta-place-name">{a.place}</span>
                <span className="ta-place-meta">{placeMeta(a)}</span>
              </div>
              <h1 className="ta-title">{a.title}</h1>
            </div>
          </header>

          <div className="ta-body">
            <div className="wrap-read">
              {a.standfirst && <p className="ta-standfirst">{a.standfirst}</p>}
              <div className="ta-meta-line">Travelled {a.date}</div>
              {a.body.map((b, i) => <Block key={i} b={b} />)}
              <div className="es-end" aria-hidden="true">&#10038; · &#10038; · &#10038;</div>
            </div>
          </div>

          <nav className="ta-next">
            <div className="wrap-read">
              <a className="ta-next-all" href="/travel" onClick={toTravel}>← All field notes</a>
            </div>
          </nav>
        </article>
        <Contact />
      </main>
    </>
  );
};

export default TravelArticlePage;
